import { getGlobalFontManager, VideoData } from '@lumen5/luminary';
import type Luminary from '@lumen5/luminary';

import { sceneDisplayId } from 'js/src/agent/tools/scene-addressing';
import { intToHex } from 'js/src/libs/color-utils';
import type { HtmlAsset } from 'js/src/luminary/html-asset-operations';

/**
 * What the subagents that design one scene's HTML (charts, layered graphics) are told about the
 * renderer and the video, in place of the video state they don't see.
 */

/** The renderer facts a model can't guess, as the bullets of a prompt section. */
export const SCENE_HTML_RENDERING_RULES = `- It is a complete \`<!DOCTYPE html>\` document sized to the frame. Make \`html\` and \`body\` fill it.
- It is rasterized frame by frame by seeking one paused GSAP timeline, never by playing it. Build that timeline in an inline script and finish with \`window.__timelines = { main: tl }\`. Time on it is in seconds, from 0 to the scene's duration.
- Animate only through that timeline: no CSS animations or transitions, no timers, no \`Date.now\` or \`Math.random\`.
- GSAP is already injected as the \`gsap\` global, with the CustomEase, DrawSVG, MorphSVG, MotionPath, SplitText and Text plugins. Nothing external loads: no \`<script src>\`, stylesheets, web fonts or images by URL.
- To scale or rotate an SVG element about a point given in the SVG's own coordinates, use \`svgOrigin: "x y"\`. \`transformOrigin\` is measured from the element's own box (\`"50% 50%"\` is its centre), so passing it SVG coordinates puts the pivot somewhere else and the shape slides across the frame as it scales.
- Use the video's colors and fonts through their CSS variables, which are injected for you.`;

/** The frame, the scenes' durations, and the palette and fonts behind the injected CSS variables. */
export function describeVideo(luminary: Luminary, sceneIds: string[]): string {
    const { width, height } = luminary.referenceResolution;
    const scenes = sceneIds.flatMap((sceneId) => {
        const scene = luminary.scenes.find((candidate) => candidate.id === sceneId);
        return scene
            ? [
                  `scene_id ${sceneDisplayId(luminary, sceneId)}: ${(scene.duration / 1000).toFixed(1)}s`,
              ]
            : [];
    });
    const colors = (luminary.template?.colors ?? []).map(
        (slot, index) => `var(--l5-color-${index}) = ${intToHex(slot.color)}`,
    );
    const fontManager = getGlobalFontManager();
    const fonts = (luminary.template?.fonts ?? []).map((url, index) => {
        const font = fontManager.getFontBySource(url) ?? fontManager.addFont(url);
        return `var(--l5-font-${index}) = ${font?.familyName ?? 'a template font'}`;
    });
    return [
        `- Frame: ${width}×${height}px.`,
        `- Your scene${scenes.length > 1 ? 's' : ''}: ${scenes.join('; ')}.`,
        `- Colors: ${colors.join(', ') || 'none'}.`,
        `- Fonts: ${fonts.join(', ') || 'none'}.`,
    ].join('\n');
}

/** The HTML already on scenes being revised; empty for new ones. */
export function currentDesign(luminary: Luminary, sceneIds: string[]): string {
    return sceneIds
        .flatMap((sceneId) => {
            const scene = luminary.scenes.find((candidate) => candidate.id === sceneId);
            const asset = scene?.getAssetsByType(VideoData.HTML_ASSET_TYPE)[0] as
                | HtmlAsset
                | undefined;
            return asset?.html
                ? [`### scene_id ${sceneDisplayId(luminary, sceneId)}\n${asset.html}`]
                : [];
        })
        .join('\n\n');
}
